/**
 * Générateur — Événements contraires et calcul de probabilités.
 * Attendu ID : 167 | Niveau : 4ème | Domaine : Probabilités
 *
 * PLAFOND (attendus de fin de 4e, Eduscol, `.claude/reference/eduscol-attendus-fin-4e.md`) :
 *   l. 214 « Il reconnaît des événements contraires et s'en sert pour calculer
 *          des probabilités. »
 *   l. 235 (exemple de réussite) « Sachant que la probabilité de gagner à un
 *          jeu est égale 0,4 calcule la probabilité de perdre. »
 * Cours du prof (19Proba, p. 3) : le contraire se calcule DANS la forme de la
 * donnée, « 100 % − 48 % = 52 % ».
 *
 * Revue 2026-09-25 (vague « sept objectifs 4e fermés », décisions du
 * coordinateur, DECISIONS_167-170.md) : le MINIMUM de la ligne 214, une carte,
 * un geste : la probabilité d'un événement est DONNÉE par un nombre, l'élève
 * reconnaît que l'événement demandé est son contraire et calcule 1 − p, dans
 * la forme de la donnée.
 *
 * UNE carte « Gagner ou perdre » (id `contraire`) :
 *   - contraire_texte (Saisie) : la donnée en écriture décimale (un ou deux
 *     chiffres) ou en pourcentage entier, jamais 0 ni 1 ; la réponse dans la
 *     même forme (« Donne-la en écriture décimale » / « en pourcentage »,
 *     suffixe % affiché) ; au moins un tirage sur deux à deux chiffres ou à un
 *     pourcentage non multiple de 10 (70 % des tirages par construction).
 *   - contraire_qcm_unique (QCM, LE QCM de l'attendu) : la donnée sous une
 *     forme tirée parmi décimal (deux chiffres non nuls : à un chiffre, toutes
 *     les familles restantes sont au-dessus de 1, écart signalé), pourcentage,
 *     fraction à dénominateur 3 à 10 ;
 *     quatre options dans la forme de la donnée, distinctes par construction,
 *     jamais 0 ni 1 (elles trahissaient la clé), au plus une option
 *     supérieure à 1 ; familles nommées dans REGLES_DISTRACTEURS_QCM et
 *     codées dans _meta.params.distracteurs.
 * Contextes : des jeux de hasard seulement (un jeu, un jeton Gagné / Perdu, une
 * roue à secteurs rouges et bleus, une urne à boules orange et vertes) ; « la
 * probabilité que … soit … » au subjonctif ; « orange » invariable.
 * Ni rappel brouillon (100 − 48 se fait de tête, comme le cours) ni calculatrice.
 *
 * SORTI (et pourquoi) : les trois saisies par forme et les deux formats
 * « Gagner ou perdre » de juillet (même geste, fusionnés ici) ; la carte « La
 * somme fait 1 » (QCM à clé constante « 1 », règle écrite dans l'énoncé,
 * Vrai/Faux à lignes déductibles et « 1,6400000000000001 » mesuré) ; les
 * contextes météo et contrôle surprise (pas des jeux de hasard) ; les options
 * « 0 » et « 1 » ; l'exigence « fraction irréductible » ; le contexte abstrait
 * « Lors d'une expérience aléatoire » ; « la probabilité que … est » (indicatif)
 * et « de que ».
 * SACRIFIÉS (jamais tirés) : 0,4 et 40 % (plafond l. 235), 0,48 et 48 % (cours
 * p. 3), la fraction 2/5 (= 0,4) ; 0,5 et 50 % ne sortent pas non plus (le
 * contraire y vaut la donnée : le QCM n'aurait plus de distracteur
 * « même valeur »).
 * Second chemin : la donnée est RELUE dans l'énoncé rendu et p + réponse = 1
 * en centièmes (ou a + réponse = b pour une fraction), throw sinon.
 * Solveur test-side : tests/solvers/probabilites-4e.js ; auto-test :
 * tests/gen167.self.test.js.
 */
import { randomChoice, randInt, makeQcm, makeSaisie, fracTex } from '../../../utils';
import { pgcd, frCent } from './probas';

const ATTENDU_ID = 167;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CONTRAIRE = 'Gagner ou perdre';

// ─── Sacrifiés (en centièmes) et exclusions par construction ──

/** 40 (plafond), 48 (cours), 50 (le contraire vaut la donnée). */
const CENTIEMES_EXCLUS = new Set([40, 48, 50]);

/** Une probabilité en centièmes, de 5 à 95, hors sacrifiés ; un chiffre (multiple de 10) dans trois tirages sur dix. */
const tirerCentiemes = () => {
    for (let essai = 0; essai < 100; essai++) {
        const unChiffre = randInt(1, 10) <= 3;
        const c = unChiffre ? 10 * randInt(1, 9) : randInt(5, 95);
        if (!CENTIEMES_EXCLUS.has(c) && (unChiffre || c % 10 !== 0)) return c;
    }
    return 35;   // repli déterministe vérifié (deux chiffres, hors sacrifiés)
};

/**
 * La donnée DÉCIMALE du QCM : deux chiffres non nuls (11 à 99, chiffres ≥ 1),
 * hors 55 (le complément chiffre à chiffre y vaut la donnée) et hors
 * sacrifiés. À un chiffre (0,3), les seules familles restantes (« 70 »,
 * « 1,3 », « 99,7 ») sont toutes au-dessus de 1 : deux options s'élimineraient
 * d'un coup d'œil. Écart à DECISIONS_167-170.md signalé : les décimaux à un
 * chiffre restent dans la Saisie et dans la forme pourcentage du QCM (30 %).
 */
const tirerCentiemesQcmDecimal = () => {
    for (let essai = 0; essai < 100; essai++) {
        const c = 10 * randInt(1, 9) + randInt(1, 9);
        if (c <= 95 && !CENTIEMES_EXCLUS.has(c) && c !== 55) return c;
    }
    return 35;
};

/**
 * Les fractions a/b admises comme donnée : b de 3 à 10, a de 2 à b − 1, a et b
 * premiers entre eux, 2a ≠ b + 1 (sinon « 1 ôté du numérateur » vaut la clé),
 * jamais 2/5 (= 0,4). Liste FIGÉE, énumérée à la main : 3/4, 4/5, 5/6, 2/7,
 * 3/7, 5/7, 6/7, 3/8, 5/8, 7/8, 2/9, 4/9, 7/9, 8/9, 3/10, 7/10, 9/10.
 */
export const FRACTIONS_DONNEES = (() => {
    const out = [];
    for (let b = 3; b <= 10; b++) {
        for (let a = 2; a < b; a++) {
            if (pgcd(a, b) === 1 && 2 * a !== b + 1 && !(a === 2 && b === 5)) out.push([a, b]);
        }
    }
    return out;
})();

// ─── Contextes : des jeux de hasard ───────────────────────────

/** `donnee` : la probabilité écrite (« 0,35 », « 35 % », KaTeX d'une fraction). */
const CONTEXTES = [
    {
        id: 'jeu',
        situation: (donnee) => `<p>À un jeu de hasard, la probabilité de gagner est égale à ${donnee}.</p>`,
        question: 'Quelle est la probabilité de perdre ?',
    },
    {
        id: 'jeton',
        situation: (donnee) => `<p>Dans un sac, chaque jeton est marqué Gagné ou Perdu. La probabilité de tirer un jeton marqué Gagné est égale à ${donnee}.</p>`,
        question: 'Quelle est la probabilité de tirer un jeton marqué Perdu ?',
    },
    {
        id: 'roue',
        situation: (donnee) => `<p>Une roue de loterie a des secteurs rouges et des secteurs bleus. La probabilité que la roue s'arrête sur un secteur rouge est égale à ${donnee}.</p>`,
        question: "Quelle est la probabilité que la roue s'arrête sur un secteur bleu ?",
    },
    {
        id: 'urne',
        situation: (donnee) => `<p>Une urne contient des boules orange et des boules vertes. La probabilité de tirer une boule orange est égale à ${donnee}.</p>`,
        question: 'Quelle est la probabilité de tirer une boule verte ?',
    },
];

// ─── Second chemin : la donnée relue dans l'énoncé rendu ──────

const strip = (html) => String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** Relit « est égale à 0,35 » / « 35 % » / « \dfrac{3}{8} » ; renvoie { num, den } (den = 100 pour décimal et pourcentage). */
const relireDonnee = (texte) => {
    const t = strip(texte);
    const f = t.match(/est égale à \$\\dfrac\{(\d+)\}\{(\d+)\}\$/);
    if (f) return { num: Number(f[1]), den: Number(f[2]) };
    const p = t.match(/est égale à (\d+) %/);
    if (p) return { num: Number(p[1]), den: 100 };
    const d = t.match(/est égale à (\d+),(\d{1,2})\./);
    if (d) return { num: Number(d[1]) * 100 + Number(d[2].padEnd(2, '0')), den: 100 };
    throw new Error(`gen_167 : donnée illisible dans « ${t} »`);
};

/** Throw si la réponse n'est pas le complément de la donnée relue (arithmétique entière). */
const verifierComplement = (texte, num, den) => {
    const lue = relireDonnee(texte);
    if (lue.den !== den || lue.num + num !== den || num <= 0 || num >= den) {
        throw new Error(`gen_167 : ${lue.num}/${lue.den} + ${num}/${den} ≠ 1`);
    }
};

// ═════════════════════════════════════════════════════════════
// CARTE « Gagner ou perdre » : contraire_texte
// ═════════════════════════════════════════════════════════════

const genContraireSaisie = () => {
    const forme = randomChoice(['decimal', 'pourcentage']);
    const c = tirerCentiemes();
    const ctx = randomChoice(CONTEXTES);
    const donnee = forme === 'decimal' ? frCent(c) : `${c} %`;
    const consigne = forme === 'decimal' ? 'Donne-la en écriture décimale.' : 'Donne-la en pourcentage.';
    const texte = `${ctx.situation(donnee)}<p><b>${ctx.question} ${consigne}</b></p>`;
    verifierComplement(texte, 100 - c, 100);
    const reponse = forme === 'decimal' ? frCent(100 - c) : String(100 - c);
    const payload = makeSaisie(reponse, texte, { ...opts(L_CONTRAIRE), strict: false, suffixe: forme === 'pourcentage' ? '%' : '' });
    payload._meta.params = { forme, contexte: ctx.id, p: c, reponse: 100 - c, deuxChiffres: c % 10 !== 0 };
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Gagner ou perdre » : contraire_qcm_unique
// ═════════════════════════════════════════════════════════════

/**
 * Les distracteurs d'une donnée décimale (c centièmes) ou en pourcentage, en
 * texte affiché, avec leur valeur numérique (le quota « au plus une option
 * au-dessus de 1 » se juge sur la valeur). `meme_valeur` toujours ;
 * `complement_chiffre_a_chiffre` quand il existe (deux chiffres non nuls,
 * hors 55 où il vaut la donnée), sinon `echelle_confondue` ; le troisième est
 * tiré parmi ce qui reste, dont au plus un de `un_plus_p` / `cent_moins_decimal`.
 */
const distracteursNombre = (forme, c) => {
    const d1 = Math.floor(c / 10), d2 = c % 10;
    const ecrit = (centiemes) => (forme === 'decimal' ? frCent(centiemes) : `${centiemes} %`);
    const chiffreAChiffre = d1 >= 1 && d2 >= 1 && !(d1 === 5 && d2 === 5) ? (10 - d1) * 10 + (10 - d2) : null;
    const familles = {
        meme_valeur: { texte: ecrit(c), valeur: c / 100 },
        complement_chiffre_a_chiffre: chiffreAChiffre === null ? null : { texte: ecrit(chiffreAChiffre), valeur: chiffreAChiffre / 100 },
        // Pour un pourcentage, « 0,65 » aurait la VALEUR de la clé « 65 % » (une
        // deuxième bonne réponse) : le complément fait sur l'échelle de 1 garde
        // ici le signe %, « 0,65 % » (écart à DECISIONS_167-170.md, signalé).
        echelle_confondue: forme === 'decimal'
            ? { texte: String(100 - c), valeur: 100 - c }
            : { texte: `${frCent(100 - c)} %`, valeur: (100 - c) / 10000 },
        un_plus_p: forme === 'decimal'
            ? { texte: frCent(100 + c), valeur: (100 + c) / 100 }
            : { texte: `${100 + c} %`, valeur: (100 + c) / 100 },
        cent_moins_decimal: forme === 'decimal'
            ? { texte: frCent(10000 - c), valeur: (10000 - c) / 100 }
            : { texte: `${c - 1} %`, valeur: (c - 1) / 100 },
    };
    const pris = ['meme_valeur', familles.complement_chiffre_a_chiffre ? 'complement_chiffre_a_chiffre' : 'echelle_confondue'];
    const reste = ['echelle_confondue', 'un_plus_p', 'cent_moins_decimal'].filter((k) => !pris.includes(k));
    pris.push(randomChoice(reste));
    return pris.map((code) => ({ code, ...familles[code] }));
};

/** Les distracteurs d'une donnée a/b : même valeur, 1 ôté du numérateur, l'inverse (la seule option au-dessus de 1). */
const distracteursFraction = (a, b) => [
    { code: 'meme_valeur', texte: fracTex(a, b), valeur: a / b },
    { code: 'un_ote_du_numerateur', texte: fracTex(a - 1, b), valeur: (a - 1) / b },
    { code: 'inverse', texte: fracTex(b, a), valeur: b / a },
];

const genContraireQcm = () => {
    const forme = randomChoice(['decimal', 'pourcentage', 'fraction']);
    const ctx = randomChoice(CONTEXTES);
    let donnee; let cle; let distracteurs; let params;
    if (forme === 'fraction') {
        const [a, b] = randomChoice(FRACTIONS_DONNEES);
        donnee = fracTex(a, b);
        cle = { texte: fracTex(b - a, b), valeur: (b - a) / b };
        distracteurs = distracteursFraction(a, b);
        params = { a, b, reponse: `${b - a}/${b}` };
    } else {
        const c = forme === 'decimal' ? tirerCentiemesQcmDecimal() : tirerCentiemes();
        donnee = forme === 'decimal' ? frCent(c) : `${c} %`;
        cle = { texte: forme === 'decimal' ? frCent(100 - c) : `${100 - c} %`, valeur: (100 - c) / 100 };
        distracteurs = distracteursNombre(forme, c);
        params = { p: c, reponse: 100 - c, deuxChiffres: c % 10 !== 0 };
    }
    const options = [cle, ...distracteurs];
    // Second chemin : quatre textes distincts, quatre valeurs distinctes, une
    // seule option égale au complément, au plus une option au-dessus de 1,
    // jamais 0 ni 1 parmi les options.
    if (new Set(options.map((o) => o.texte)).size !== 4) throw new Error('gen_167 : options en double');
    if (new Set(options.map((o) => o.valeur.toFixed(6))).size !== 4) throw new Error('gen_167 : valeurs en double');
    if (options.filter((o) => o.valeur > 1).length > 1) throw new Error('gen_167 : deux options au-dessus de 1');
    if (options.some((o) => o.valeur === 0 || o.valeur === 1)) throw new Error('gen_167 : option 0 ou 1');
    const texte = `${ctx.situation(donnee)}<p><b>${ctx.question}</b></p>`;
    const lue = relireDonnee(texte);
    if (Math.abs(lue.num / lue.den + cle.valeur - 1) > 1e-9) throw new Error('gen_167 : la clé n\'est pas le complément');
    const payload = makeQcm(options.map((o) => o.texte), cle.texte, texte, opts(L_CONTRAIRE));
    payload._meta.params = { forme, contexte: ctx.id, ...params, distracteurs: distracteurs.map((d) => d.code) };
    return payload;
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    contraire_qcm_unique: {
        support: 'la probabilité d\'un événement d\'un jeu de hasard (gagner, jeton Gagné, secteur rouge, boule orange) est donnée en décimal (5 à 95 centièmes), en pourcentage entier (5 à 95) ou en fraction (dénominateur 3 à 10, numérateur 2 au moins) ; « Quelle est la probabilité de [l\'événement contraire] ? » ; quatre options dans la forme de la donnée',
        avertissement: 'AUCUN item DEPP ou Eduscol lu ne chiffre une erreur sur l\'événement contraire : ces familles sont des HYPOTHÈSES D\'AUTEUR (cadrage 2026-09-25), à confirmer par le prof',
        familles: {
            meme_valeur: 'le contraire « a la même chance » : la donnée recopiée (toujours présente ; généralisation abusive du pile ou face de CM1)',
            complement_chiffre_a_chiffre: 'chaque chiffre complété à 10 sans retenue : 1 − 0,35 = 0,75, 100 − 45 = 65 ; n\'existe qu\'à deux chiffres non nuls (hors 0,55 / 55 %, où il vaut la donnée) ; à un chiffre il vaudrait la clé (hors support). ÉCART signalé : la forme décimale du QCM ne tire que des données à deux chiffres non nuls, car à un chiffre les trois autres familles (« 70 », « 1,3 », « 99,7 ») sont toutes au-dessus de 1 ; les décimaux à un chiffre restent dans la Saisie et la forme pourcentage garde ses multiples de 10',
            echelle_confondue: 'les deux échelles mélangées : pour 0,35 le complément à 100, « 65 » ; pour 35 % le complément à 1 avec le signe % gardé, « 0,65 % » (DECISIONS proposait « 0,65 », qui vaut 65 % : une deuxième bonne réponse, écart signalé)',
            un_plus_p: '1 + p au lieu de 1 − p : 1,35 ; 135 % (l\'option au-dessus de 1, au plus une par QCM, avec cent_moins_decimal)',
            cent_moins_decimal: '100 − 0,35 = 99,65 pour un décimal ; 1 − 35 = 34 % pour un pourcentage (le 1 et le 100 confondus)',
            un_ote_du_numerateur: '(fractions) 1 − 3/8 = 2/8 : le 1 ôté du numérateur (parent : DEPP 2de 2025 q.8, « soustrait les numérateurs et les dénominateurs »)',
            inverse: '(fractions) 8/3 pour 3/8 (DEPP 4e 2023 q.15 ; fiche 5e 1082) ; la seule option au-dessus de 1',
        },
        garantie: 'quatre textes et quatre valeurs deux à deux distincts, une seule option égale au complément de la donnée RELUE dans l\'énoncé, au plus une option au-dessus de 1, jamais 0 ni 1 (throw sinon) ; fractions données figées (FRACTIONS_DONNEES : premières entre elles, 2a ≠ b + 1 pour que « 1 ôté du numérateur » ne vaille pas la clé, 2/5 sacrifié)',
        supprimees: 'les options « 0 », « 1 », « 100 % », « 0 % » de juillet (absurdes : la clé se repérait comme « ni la donnée ni une borne », fuite mesurée 100 %)',
    },
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    contraire_texte: genContraireSaisie,
    contraire_qcm_unique: genContraireQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'contraire', label: L_CONTRAIRE, formatsList: buildFormats('contraire') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

/** Fonctions internes exposées à l'auto-test (énumération exhaustive des tirages). */
export const _interne = { tirerCentiemes, tirerCentiemesQcmDecimal, distracteursNombre, distracteursFraction, CENTIEMES_EXCLUS };
